import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type TeamProfile, type TeamProfileBody } from "@/lib/api";

export const teamProfilesKey = ["teamProfiles"] as const;

/**
 * Org-level team profiles (Pass 7c).
 *
 * Read access is open to all members — they need it to power the picker
 * inside personnel-kind sections. Mutations are admin-only on the server;
 * the UI hides the affordances for non-admins.
 */
export function useTeamProfiles() {
  return useQuery({
    queryKey: teamProfilesKey,
    queryFn: () => api.listTeamProfiles(),
    staleTime: 60_000,
  });
}

export function useCreateTeamProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: TeamProfileBody): Promise<TeamProfile> => api.createTeamProfile(body),
    onSuccess() {
      qc.invalidateQueries({ queryKey: teamProfilesKey });
    },
  });
}

export function useUpdateTeamProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      profileId,
      patch,
    }: {
      profileId: string;
      patch: Partial<TeamProfileBody>;
    }): Promise<TeamProfile> => api.updateTeamProfile(profileId, patch),
    onSuccess() {
      qc.invalidateQueries({ queryKey: teamProfilesKey });
    },
  });
}

export function useDeleteTeamProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (profileId: string) => api.deleteTeamProfile(profileId),
    onSuccess() {
      qc.invalidateQueries({ queryKey: teamProfilesKey });
    },
  });
}
